/**
 * HEALTH PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Dark tool mode.
 *
 * Note: This page has complex integration requirements (Oura, Apple Health).
 * We preserve existing logic while wrapping with Screen primitive for consistency.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Screen } from '../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import HealthMetricsList, { HealthMetricsListRef } from '../components/HealthMetricsList';
import HealthEmptyState from '../components/HealthEmptyState';
import SubjectiveCheckInForm from '../components/SubjectiveCheckInForm';
import HealthDataExport from '../components/HealthDataExport';
import CoachDashboard, { CoachDashboardPreloadedData } from '../components/CoachDashboard';
import HealthGoalsSummary from '../components/HealthGoalsSummary';
import WorkoutSessionForm from '../components/WorkoutSessionForm';
import StrengthSessionForm from '../components/StrengthSessionForm';
import BodyCompForm from '../components/BodyCompForm';
import { Modal } from '../design/primitives';
import { Button, Alert, Spinner } from '../components/ui';
import { ArrowPathIcon, ChartBarIcon, TableCellsIcon, ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon, FlagIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../api/apiConfig';

const HealthPage: React.FC = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [ouraConnected, setOuraConnected] = useState(false);
  const [ouraLastSync, setOuraLastSync] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [appleHealthConnected, setAppleHealthConnected] = useState(false);
  const [appleHealthLastSync, setAppleHealthLastSync] = useState<string | null>(null);
  const [healthfitConnected, setHealthfitConnected] = useState(false);
  const [healthfitLastSync, setHealthfitLastSync] = useState<string | null>(null);
  const [healthfitSyncing, setHealthfitSyncing] = useState(false);
  const [hevyConnected, setHevyConnected] = useState(false);
  const [hevyLastSync, setHevyLastSync] = useState<string | null>(null);
  const [hevySyncing, setHevySyncing] = useState(false);
  const [dataQualityWarning, setDataQualityWarning] = useState<{
    type: string;
    message: string;
    action: string;
    date: string;
  } | null>(null);
  const [viewMode, setViewMode] = useState<'dashboard' | 'table' | 'goals'>('dashboard');
  const [healthGoals, setHealthGoals] = useState<any[] | null>(null);
  const [metricsStats, setMetricsStats] = useState<{total: number; recent: number} | null>(null);
  const [coachDashboardData, setCoachDashboardData] = useState<CoachDashboardPreloadedData | null>(null);
  const [workoutFormOpen, setWorkoutFormOpen] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [strengthFormOpen, setStrengthFormOpen] = useState(false);
  const [bodyCompFormOpen, setBodyCompFormOpen] = useState(false);
  const healthMetricsListRef = useRef<HealthMetricsListRef>(null);

  const handleOuraSync = useCallback(async () => {
    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);

      const response = await apiClient.post('/api/oura/sync/');
      const details = response.data.details;
      const detailsMsg = details
        ? ` (Activity: ${details.activity_records}, Sleep: ${details.sleep_records}, Readiness: ${details.readiness_records})`
        : '';

      let message = `Synced ${response.data.imported_count} health metrics from Oura Ring!${detailsMsg}`;

      if (response.data.imported_count === 0 && details) {
        message += ` - ${details.sleep_in_db || 0} sleep metrics already in database`;
      }

      setSuccess(message);
      setOuraLastSync(response.data.last_synced_at);

      healthMetricsListRef.current?.refresh();
      fetchMetricsStats();
    } catch (error: any) {
      console.error('Error syncing Oura data:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to sync Oura data';
      setError(errorMessage);
    } finally {
      setSyncing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkOuraStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/oura/status/');
      setOuraConnected(response.data.connected);
      setOuraLastSync(response.data.last_synced_at);
    } catch (err) {
      console.error('Error checking Oura status:', err);
    }
  }, []);

  const fetchHealthSourcesStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/health-sources/status/');
      const sources = response.data.sources || [];

      // Update individual source states
      const oura = sources.find((s: any) => s.source === 'oura_ring');
      const healthfit = sources.find((s: any) => s.source === 'healthfit');
      const healthExport = sources.find((s: any) => s.source === 'health_export');

      if (oura) {
        setOuraConnected(oura.connected);
        setOuraLastSync(oura.last_synced_at);
      }
      if (healthfit) {
        setHealthfitConnected(healthfit.connected);
        setHealthfitLastSync(healthfit.last_synced_at);
      }
      if (healthExport) {
        setAppleHealthConnected(healthExport.connected);
        setAppleHealthLastSync(healthExport.last_synced_at);
      }

      // Fetch Hevy status separately
      try {
        const hevyResponse = await apiClient.get('/api/hevy/status/');
        setHevyConnected(hevyResponse.data.connected);
        setHevyLastSync(hevyResponse.data.last_synced_at);
      } catch (err) {
        // Hevy not connected or error
        setHevyConnected(false);
      }

      // Check for data quality warnings
      setDataQualityWarning(response.data.data_quality_warning || null);
    } catch (err) {
      console.error('Error fetching health sources status:', err);
    }
  }, []);

  const fetchMetricsStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/health-metrics/summary/');
      const total = response.data.total_metrics || 0;
      // Recent is last 7 days - calculate from the summary
      setMetricsStats({ total, recent: 0 }); // TODO: Add recent count to API
    } catch (err) {
      console.error('Error fetching metrics stats:', err);
    }
  }, []);

  const fetchCoachDashboardData = useCallback(async () => {
    try {
      const [recoveryRes, dashboardRes, nutritionRes, coachingRes, weightGoalsRes, processGoalsRes] = await Promise.all([
        apiClient.get('/api/recovery-index/'),
        apiClient.get('/api/daily-health-aggregates/dashboard/?days=90'),
        apiClient.get('/api/nutrition-snapshots/?days=7'),
        apiClient.get('/api/coaching/status/').catch(() => ({ data: null })),
        apiClient.get('/api/health-goals/', { params: { metric_type: 'weight', status: 'active' } }).catch(() => ({ data: [] })),
        apiClient.get('/api/health-goals/', { params: { goal_type: 'process', status: 'active' } }).catch(() => ({ data: [] }))
      ]);

      setCoachDashboardData({
        recovery: recoveryRes.data,
        dashboard: dashboardRes.data,
        nutritionSnapshots: Array.isArray(nutritionRes.data) ? nutritionRes.data : (nutritionRes.data.results || []),
        coaching: coachingRes.data,
        weightGoals: weightGoalsRes.data.results || weightGoalsRes.data || [],
        processGoals: processGoalsRes.data.results || processGoalsRes.data || []
      });
    } catch (err) {
      console.error('Error fetching coach dashboard data:', err);
    }
  }, []);

  const fetchHealthGoals = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/health-goals/', {
        params: { status: 'active' },
      });
      setHealthGoals(response.data.results || response.data);
    } catch (err) {
      console.error('Error fetching health goals:', err);
      setHealthGoals([]);
    }
  }, []);

  const handleHealthfitSync = useCallback(async () => {
    try {
      setHealthfitSyncing(true);
      setError(null);
      setSuccess(null);

      const response = await apiClient.post('/api/healthfit/sync/');

      const daysAggregated = response.data.days_aggregated || 0;
      const message = `Synced ${response.data.imported_count || 0} metrics from HealthFit (${response.data.workout_sessions_created || 0} workouts)${daysAggregated > 0 ? `, re-aggregated ${daysAggregated} days` : ''}`;
      setSuccess(message);
      setHealthfitLastSync(response.data.last_synced_at);

      // Clear data quality warning since sync fixes it
      setDataQualityWarning(null);

      healthMetricsListRef.current?.refresh();
      fetchCoachDashboardData();
    } catch (error: any) {
      console.error('Error syncing HealthFit data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to sync HealthFit data';
      setError(errorMessage);
    } finally {
      setHealthfitSyncing(false);
    }
  }, [fetchCoachDashboardData]);

  const handleHevySync = useCallback(async () => {
    try {
      setHevySyncing(true);
      setError(null);
      setSuccess(null);

      const response = await apiClient.post('/api/hevy/sync/');

      const created = response.data.workout_sessions_created || 0;
      const skipped = response.data.workout_sessions_skipped || 0;
      const fetched = response.data.workouts_fetched || 0;
      const strengthCreated = response.data.strength_sessions_created || 0;

      let message = '';
      if (created > 0) {
        message = `Synced ${created} new workout${created !== 1 ? 's' : ''} from Hevy`;
      } else if (fetched > 0 && skipped > 0) {
        message = `Hevy is up to date (${skipped} workout${skipped !== 1 ? 's' : ''} already synced)`;
      } else {
        message = 'No new workouts found in Hevy';
      }
      if (strengthCreated > 0) {
        message += `, ${strengthCreated} strength session${strengthCreated !== 1 ? 's' : ''} added`;
      }
      setSuccess(message);
      if (response.data.last_sync) {
        setHevyLastSync(response.data.last_sync);
      }

      healthMetricsListRef.current?.refresh();
      fetchCoachDashboardData();
    } catch (error: any) {
      console.error('Error syncing Hevy data:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to sync Hevy data';
      setError(errorMessage);
    } finally {
      setHevySyncing(false);
    }
  }, [fetchCoachDashboardData]);

  const handleOuraCallback = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const ouraStatus = params.get('oura');

    if (ouraStatus === 'success') {
      setSuccess('Oura Ring connected successfully!');
      setOuraConnected(true);
      window.history.replaceState({}, '', '/health');
      handleOuraSync();
    } else if (ouraStatus === 'error') {
      const message = params.get('message') || 'Unknown error';
      setError(`Oura connection failed: ${message.replace(/_/g, ' ')}`);
      window.history.replaceState({}, '', '/health');
    }
  }, [handleOuraSync]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await Promise.all([
          fetchHealthSourcesStatus(),
          fetchMetricsStats(),
          fetchCoachDashboardData(),
          fetchHealthGoals(),
        ]);
      } finally {
        setInitialLoading(false);
      }
    };
    loadInitialData();
    handleOuraCallback();
  }, [fetchHealthSourcesStatus, fetchMetricsStats, fetchCoachDashboardData, fetchHealthGoals, handleOuraCallback]);

  const handleConnectOura = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/api/oura/authorize/');
      window.location.href = response.data.authorization_url;
    } catch (error: any) {
      console.error('Error connecting to Oura:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to connect to Oura';
      setError(errorMessage);
      setLoading(false);
    }
  };

  // Show full-page loading spinner until initial data is loaded
  // Check both initialLoading flag AND key data to handle React's async state updates
  if (initialLoading || metricsStats === null || coachDashboardData === null) {
    return (
      <Screen>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          gap: SPACING[16],
        }}>
          <Spinner size="lg" />
          <span style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.text.secondary,
          }}>
            Loading health data...
          </span>
        </div>
      </Screen>
    );
  }

  return (
    <Screen>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h1,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
          }}>Health & Fitness</h1>
          <div style={{ display: 'flex', gap: SPACING[8], alignItems: 'center' }}>
            {/* Compact source indicators with sync */}
            {[
              { label: 'Oura', connected: ouraConnected, lastSync: ouraLastSync, syncing: syncing, onSync: ouraConnected ? handleOuraSync : handleConnectOura },
              { label: 'HealthFit', connected: healthfitConnected, lastSync: healthfitLastSync, syncing: healthfitSyncing, onSync: handleHealthfitSync },
              { label: 'Hevy', connected: hevyConnected, lastSync: hevyLastSync, syncing: hevySyncing, onSync: handleHevySync, hidden: !hevyConnected },
              { label: 'Export', connected: appleHealthConnected, lastSync: appleHealthLastSync, auto: true },
            ].filter(s => !s.hidden).map(source => (
              <div
                key={source.label}
                onClick={source.auto ? undefined : source.onSync}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING[4],
                  padding: `${SPACING[4]} ${SPACING[8]}`,
                  backgroundColor: COLORS.bg.base,
                  borderRadius: RADIUS,
                  cursor: source.auto ? 'default' : 'pointer',
                  opacity: source.syncing ? 0.6 : 1,
                  border: `1px solid ${source.connected ? COLORS.status.success + '40' : COLORS.border.default}`,
                }}
              >
                {source.connected ? (
                  <CheckCircleIcon style={{ width: '12px', height: '12px', color: COLORS.status.success, flexShrink: 0 }} />
                ) : (
                  <XCircleIcon style={{ width: '12px', height: '12px', color: COLORS.text.tertiary, flexShrink: 0 }} />
                )}
                <span style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: '600',
                  color: COLORS.text.primary,
                }}>{source.label}</span>
                {source.connected && source.lastSync && (
                  <span style={{
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.size.xs,
                    color: COLORS.text.tertiary,
                  }}>
                    {(() => {
                      const syncDate = new Date(source.lastSync);
                      const now = new Date();
                      const diffMs = now.getTime() - syncDate.getTime();
                      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                      const diffMins = Math.floor(diffMs / (1000 * 60));
                      if (diffMins < 60) return `${diffMins}m`;
                      if (diffHours < 24) return `${diffHours}h`;
                      return `${Math.floor(diffHours / 24)}d`;
                    })()}
                  </span>
                )}
                {source.syncing && <ArrowPathIcon style={{ width: '12px', height: '12px', color: COLORS.text.secondary, animation: 'spin 1s linear infinite' }} />}
                {source.auto && <span style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>auto</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Alerts */}
        {error && <Alert variant="error">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Data Quality Warning Banner */}
        {dataQualityWarning && (
          <div style={{
            padding: `${SPACING[8]} ${SPACING[12]}`,
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            border: `1px solid ${COLORS.status.warning}`,
            borderRadius: RADIUS,
            display: 'flex',
            alignItems: 'center',
            gap: SPACING[8],
          }}>
            <ExclamationTriangleIcon style={{ width: 16, height: 16, color: COLORS.status.warning, flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.primary }}>
              {dataQualityWarning.message}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleHealthfitSync}
              disabled={healthfitSyncing}
              isLoading={healthfitSyncing}
              loadingText="Syncing..."
            >
              {dataQualityWarning.action}
            </Button>
          </div>
        )}

        {/* View Mode Toggle - simplified to 2 tabs */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACING[4],
          borderBottom: `1px solid ${COLORS.border.default}`,
        }}>
          {[
            { mode: 'dashboard' as const, icon: ChartBarIcon, label: 'Dashboard' },
            { mode: 'goals' as const, icon: FlagIcon, label: 'Goals' },
            { mode: 'table' as const, icon: TableCellsIcon, label: 'Raw Data' },
          ].map(({ mode, icon: Icon, label }) => {
            const isActive = viewMode === mode;
            return (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING[8],
                  padding: `${SPACING[8]} ${SPACING[16]}`,
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: isActive ? COLORS.accent.default : COLORS.text.secondary,
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: isActive ? `2px solid ${COLORS.accent.default}` : '2px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 0.1s ease',
                  marginBottom: '-1px',
                }}
              >
                <Icon style={{ width: '16px', height: '16px' }} />
                {label}
              </button>
            );
          })}
        </div>

        {/* Conditional View Rendering */}
        {viewMode === 'dashboard' ? (
          metricsStats.total > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
              {/* Coach Dashboard - 9 Tiles */}
              <CoachDashboard
                onOpenWorkoutForm={() => setWorkoutFormOpen(true)}
                onOpenStrengthForm={() => setStrengthFormOpen(true)}
                onOpenBodyCompForm={() => setBodyCompFormOpen(true)}
                preloadedData={coachDashboardData || undefined}
              />

              {/* Compact action row */}
              <div style={{ display: 'flex', gap: SPACING[8] }}>
                <Button variant="outline" size="sm" onClick={() => setCheckInOpen(true)}>Daily Check-in</Button>
                <Button variant="outline" size="sm" onClick={() => setExportOpen(true)}>Export CSV</Button>
              </div>

              {/* Check-in Modal */}
              <Modal isOpen={checkInOpen} onClose={() => setCheckInOpen(false)} title="Daily Check-in">
                <SubjectiveCheckInForm onSuccess={() => {
                  setCheckInOpen(false);
                  setSuccess('Check-in submitted!');
                  fetchCoachDashboardData();
                  healthMetricsListRef.current?.refresh();
                  setTimeout(() => setSuccess(null), 3000);
                }} />
              </Modal>

              {/* Export Modal */}
              <Modal isOpen={exportOpen} onClose={() => setExportOpen(false)} title="Export Health Data">
                <HealthDataExport />
              </Modal>
            </div>
          ) : (
            <HealthEmptyState
              ouraConnected={ouraConnected}
              appleHealthConnected={appleHealthConnected}
              onConnectOura={handleConnectOura}
              onSyncOura={ouraConnected ? handleOuraSync : undefined}
              ouraLastSync={ouraLastSync}
              appleHealthLastSync={appleHealthLastSync}
            />
          )
        ) : viewMode === 'goals' ? (
          <HealthGoalsSummary preloadedGoals={healthGoals || undefined} />
        ) : (
          <HealthMetricsList ref={healthMetricsListRef} />
        )}
      </div>



      {/* Workout Session Form */}
      <WorkoutSessionForm
        isOpen={workoutFormOpen}
        onClose={() => setWorkoutFormOpen(false)}
        onSuccess={() => {
          fetchCoachDashboardData();
          setSuccess('Workout logged successfully!');
          setTimeout(() => setSuccess(null), 3000);
        }}
      />

      {/* Strength Session Form */}
      <StrengthSessionForm
        isOpen={strengthFormOpen}
        onClose={() => setStrengthFormOpen(false)}
        onSuccess={() => {
          fetchCoachDashboardData();
          setSuccess('Lift logged successfully!');
          setTimeout(() => setSuccess(null), 3000);
        }}
      />

      {/* Body Composition Form */}
      <BodyCompForm
        isOpen={bodyCompFormOpen}
        onClose={() => setBodyCompFormOpen(false)}
        onSuccess={() => {
          fetchCoachDashboardData();
          healthMetricsListRef.current?.refresh();
          setSuccess('Body composition logged successfully!');
          setTimeout(() => setSuccess(null), 3000);
        }}
      />
    </Screen>
  );
};

export default HealthPage;
